import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, DatePicker, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, Image, Row, Col, message, Upload } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchNewsList, createNews, updateNews, deleteNews,  fetchNewsById, updateNewsCoverImage } from '../../store/slices/newsSlice';
import { getBranches } from '../../store/slices/branchesSlice';

const { TextArea } = Input;
const { Title } = Typography;

const ensureHttps = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

const NewsPage = () => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.branches.branches);
  const selectedPlace = useSelector((state) => state.branches.selectedPlace);
  const { items, loading, pagination, bucketImages, bucketLoading } = useSelector((state) => state.news);

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [filters, setFilters] = useState({
    pageIndex: 0,
    pageSize: 10,
    branchId: null,
  });

  // branches güvenli liste
  const branchList = useMemo(() => {
    if (Array.isArray(branches)) return branches;
    if (branches && Array.isArray(branches.items)) return branches.items;
    return [];
  }, [branches]);

  const refetchList = useCallback(() => {
    const branchId = filters.branchId ?? null;
    dispatch(fetchNewsList({
      pageIndex: filters.pageIndex,
      pageSize: filters.pageSize,
      branchId,
      // Şubesi boş olan haberler ("tümü için") her şubede görünmeye devam ediyor.
      placeId: selectedPlace?.id ?? null,
    }));
  }, [dispatch, filters.pageIndex, filters.pageSize, filters.branchId, selectedPlace?.id]);

  // İlk yüklemeler
  useEffect(() => {
    if (!branches || (Array.isArray(branches) && branches.length === 0) || (branches && Array.isArray(branches.items) && branches.items.length === 0)) {
      dispatch(getBranches());
    }
  }, [dispatch]);

  // Haber listesi
  useEffect(() => {
    refetchList();
  }, [refetchList]);

  const openCreate = () => {
    setEditing(null);
    setCoverImageFile(null);
    setPreviewImage(null);
    form.resetFields();
    form.setFieldsValue({
      branchId: null,
      isActive: true,
      publishDate: dayjs(),
    });
    setModalOpen(true);
  };

  const openEdit = async (record) => {
    try {
      // Liste satırından gelen temel alanlar (lowercase/uppercase uyumlu)
      const base = {
        branchId: record.branchId ?? record.BranchId ?? null,
        title: record.title ?? record.Title ?? '',
        publishDate: record.publishDate ?? record.PublishDate ?? null,
        isActive: (record.isActive ?? record.IsActive) ?? false,
        coverImage: record.coverImage ?? record.CoverImage ?? undefined,
      };

      // İçerik listede yoksa detay çek
      let content = record.content ?? record.Content;
      if (!content && (record.id || record.Id)) {
        const detail = await dispatch(fetchNewsById(record.id || record.Id)).unwrap();
        content = detail?.content ?? detail?.Content ?? '';
        // Tarih ve diğer alanları da detaydan güncelle (varsa)
        base.title = detail?.title ?? detail?.Title ?? base.title;
        base.publishDate = detail?.publishDate ?? detail?.PublishDate ?? base.publishDate;
        base.isActive = (detail?.isActive ?? detail?.IsActive) ?? base.isActive;
        base.coverImage = detail?.coverImage ?? detail?.CoverImage ?? base.coverImage;
        base.branchId = detail?.branchId ?? detail?.BranchId ?? base.branchId;
      }

      setEditing(record);
      setCoverImageFile(null);
      setPreviewImage(base.coverImage ? ensureHttps(base.coverImage) : null);
      form.setFieldsValue({
        branchId: base.branchId ?? null,
        title: base.title,
        content: content || '',
        publishDate: base.publishDate ? dayjs(base.publishDate) : dayjs(),
        isActive: !!base.isActive,
      });
      setModalOpen(true);
    } catch (e) {
      message.error('Haber detayı alınamadı');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        BranchId: values.branchId || null,
        // Branş seçilmediyse haber tüm branşlar için; o durumda şubeye de bağlanmıyor.
        PlaceId: values.branchId ? (selectedPlace?.id ?? null) : null,
        Title: values.title,
        Content: values.content,
        PublishDate: values.publishDate?.toISOString(),
        IsActive: values.isActive,
        coverImageFile: coverImageFile,
      };

      if (editing) {
        await dispatch(updateNews({ id: editing.Id || editing.id, ...payload })).unwrap();
        message.success('Haber güncellendi');
      } else {
        await dispatch(createNews(payload)).unwrap();
        message.success('Haber oluşturuldu');
      }
      setModalOpen(false);
      form.resetFields();
      setCoverImageFile(null);
      setPreviewImage(null);
      // Listeyi tekrar çek
      refetchList();
    } catch (err) {
      message.error(err?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (record) => {
    try {
      await dispatch(deleteNews(record.Id || record.id)).unwrap();
      message.success('Haber silindi');
      refetchList();
    } catch (err) {
      message.error(err?.message || 'Silme başarısız');
    }
  };

  const handleImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Sadece resim dosyası yükleyebilirsiniz!');
      return false;
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Resim dosyası 2MB\'dan küçük olmalıdır!');
      return false;
    }

    setCoverImageFile(file);
    setPreviewImage(URL.createObjectURL(file));
    return false; // Prevent default upload behavior
  };

  const handleImageRemove = () => {
    setCoverImageFile(null);
    setPreviewImage(null);
  };

  const columns = [
    { 
      title: 'Başlık', key: 'title',
      render: (_, record) => record.title ?? record.Title ?? '-'
    },
    { 
      title: 'Görsel', 
      key: 'coverImage', 
      render: (_, record) => {
        const url = ensureHttps(record.coverImageUrl ?? record.CoverImageUrl);
        return url ? <Image src={url} width={64} height={64} style={{ objectFit: 'cover' }} /> : '-';
      }
    },
    { 
      title: 'Yayın Tarihi', key: 'publishDate',
      render: (_, record) => {
        const d = record.publishDate ?? record.PublishDate;
        return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-';
      }
    },
    { 
      title: 'Kapsam', key: 'branchId',
      render: (_, record) => {
        const bid = record.branchId ?? record.BranchId;
        if (!bid) return <Tag color="green">Tümü</Tag>;
        const branch = branchList.find(b => String(b.id) === String(bid));
        const label = branch?.name || 'Şube';
        return <Tag color="blue">{label}</Tag>;
      }
    },
    { 
      title: 'Durum', key: 'isActive',
      render: (_, record) => {
        const v = record.isActive ?? record.IsActive;
        return v ? <Tag color="green">Aktif</Tag> : <Tag color="red">Pasif</Tag>;
      }
    },
    {
      title: 'İşlemler', key: 'actions', fixed: 'right', width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" type="primary" onClick={() => openEdit(record)}>Düzenle</Button>
          <Popconfirm title="Silmek istiyor musunuz?" onConfirm={() => handleDelete(record)}>
            <Button size="small" danger>Sil</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const paginationProps = useMemo(() => ({
    current: (pagination.index || 0) + 1,
    total: pagination.count || items.length,
    pageSize: pagination.size || filters.pageSize,
    onChange: (page, pageSize) => setFilters(prev => ({ ...prev, pageIndex: page - 1, pageSize })),
    showSizeChanger: true,
    showQuickJumper: true,
  }), [pagination, items.length, filters.pageSize]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={4}>Haberler</Title>
        </Col>
        <Col>
          <Button type="primary" onClick={openCreate}>Yeni Haber</Button>
        </Col>
      </Row>

      <Card>
        <Row align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Select
              style={{ width: 260 }}
              value={filters.branchId}
              onChange={(v) => setFilters(prev => ({ ...prev, pageIndex: 0, branchId: v }))}
              options={[{ label: 'Tümü', value: null }, ...branchList.map(b => ({ label: b.name, value: b.id }))]}
            />
          </Col>
        </Row>
        <Table
          rowKey={(r) => r.id || r.Id}
          dataSource={items}
          columns={columns}
          loading={loading}
          pagination={paginationProps}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editing ? 'Haber Düzenle' : 'Yeni Haber'}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="branchId" label="Şube" tooltip="Boş bırakılırsa tümü olarak yayınlanır">
                <Select
                  allowClear
                  placeholder="Tümü"
                  options={branchList.map(b => ({ label: b.name, value: b.id }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="publishDate" label="Yayın Tarihi" rules={[{ required: true, message: 'Gerekli alan' }]}>
                <DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="title" label="Başlık" rules={[{ required: true, message: 'Gerekli alan' }, { max: 200 }]}>
            <Input placeholder="Başlık" maxLength={200} />
          </Form.Item>

          <Form.Item name="content" label="İçerik" rules={[{ required: true, message: 'Gerekli alan' }]}>
            <TextArea rows={6} placeholder="İçerik" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isActive" label="Durum" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Kapak Görseli">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {previewImage && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <Image 
                        src={previewImage} 
                        width={120} 
                        height={80} 
                        style={{ objectFit: 'cover', borderRadius: 4 }} 
                      />
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        style={{ 
                          position: 'absolute', 
                          top: -8, 
                          right: -8, 
                          backgroundColor: '#ff4d4f', 
                          color: 'white',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={handleImageRemove}
                      />
                    </div>
                  )}
                  <Upload
                    beforeUpload={handleImageUpload}
                    showUploadList={false}
                    accept="image/*"
                  >
                    <Button icon={<UploadOutlined />} disabled={!!previewImage}>
                      {previewImage ? 'Görsel Seçildi' : 'Görsel Yükle'}
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>{editing ? 'Güncelle' : 'Oluştur'}</Button>
              <Button onClick={() => setModalOpen(false)}>İptal</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default NewsPage; 